<script setup lang="ts">
  import { computed } from 'vue';
  import { useModal } from 'vue-final-modal';
  import HomologationTableRow from './HomologationTableRow.vue';
  import {
    DefaultTable,
    TableHeader,
    ConfirmationModal,
    ConfirmDeleteModal,
    HeaderTableWithButtons,
    SearchNoResults,
  } from '@/shared/ui';
  import type { DocumentRevision } from '@/entities/Document';
  import { documentApi } from '@/entities/Document';
  import { useCustomToast } from '@/shared/lib';
  import { useNotificationStore } from '@/entities/Notification';
  import { DocumentType, OrderOptions } from '@/shared/lib';
  import type { HomologationTableEmits } from '@/shared/lib';
  // Composables
  import { useTableEvents, type TableEmitter } from '@/shared/lib';
  import { matrixBuilderApi } from '@/features/FormBuilder';

  const props = defineProps<{
    revisions: DocumentRevision[];
    page: number;
    orderOptions?: OrderOptions[];
    documentType?: DocumentType;
    isFilterActive?: boolean;
  }>();

  const emit = defineEmits<HomologationTableEmits>();
  const toast = useCustomToast();
  const { fetchApprovalCount, fetchVerificationCount } = useNotificationStore();

  const { handleSortOptions, handleFilter } = useTableEvents(emit as TableEmitter);

  const isRecordTab = computed(() => props.documentType === DocumentType.REGISTRATION);
  const tableTitle = computed(() => (isRecordTab.value ? 'Registros' : 'Documentos'));

  const handleFilterClicked = () => {
    emit('filter-clicked');
  };

  const showDeleteSuccessToast = () => {
    toast.showSuccessToast({
      title: 'Documento Removido',
      message: 'Os usuários envolvidos serão notificados.',
      type: 'success',
    });
  };

  const showDeleteErrorToast = () => {
    toast.showErrorToast({
      title: 'Erro ao Remover Documento',
      message: 'Houve um erro ao remover o documento, tente novamente mais tarde.',
      type: 'error',
    });
  };

  const showRemoveRecordSuccessToast = () => {
    toast.showSuccessToast({
      title: 'Registro deletado com sucesso!',
      message: '',
      type: 'success',
    });
  };

  const showRemoveRecordErrorToast = () => {
    toast.showErrorToast({
      title: 'Erro ao deletar Registro.',
      message: 'Tente novamente',
      type: 'error',
    });
  };

  const refreshHomologationCounters = async () => {
    await fetchApprovalCount();
    await fetchVerificationCount();
  };

  const openDocumentRemovalModal = (id: number) => {
    const { open: openConfirmation, close: closeConfirmation } = useModal({
      component: ConfirmationModal,
      attrs: {
        title: 'Tem certeza que deseja remover o documento da Homologação?',
        text: '',
        actionButtonText: 'Remover',
        cancelButtonText: 'Cancelar',
        onClose() {
          closeConfirmation();
        },
        async onConfirm() {
          await handleDeleteFrontend(id);
          closeConfirmation();
          await refreshHomologationCounters();
        },
      },
    });

    openConfirmation();
  };

  const openRecordRemovalModal = (id: number) => {
    const { open: openConfirmation, close: closeConfirmation } = useModal({
      component: ConfirmDeleteModal,
      attrs: {
        title: 'Tem certeza que deseja deletar o Registro?',
        description: 'A ação não pode ser desfeita.',
        onClose() {
          closeConfirmation();
        },
        async onConfirm() {
          await handleRemoveRecord(id);
          closeConfirmation();
          await refreshHomologationCounters();
        },
      },
    });

    openConfirmation();
  };

  const confirmDelete = (id: number) => {
    if (!props.revisions) throw new Error('Não há homologações para deletar');
    if (!id) throw new Error('ID não definido');

    if (isRecordTab.value) {
      openRecordRemovalModal(id);
      return;
    }

    openDocumentRemovalModal(id);
  };

  const handleDeleteFrontend = async (id: number) => {
    try {
      await documentApi.deleteDocumentRevision(id);
      emit('delete', [id]);

      showDeleteSuccessToast();
    } catch {
      showDeleteErrorToast();
    }
  };

  /**
   * Deletar um Registro em homologação apaga a linha de `matrices`, e nao a reprova: o backend
   * aceita a exclusao nos tres status da aba, Aguardando VG incluido, e o `ON DELETE CASCADE` leva
   * aprovacao, verificacao e changelog junto. E por isso que o modal avisa que nao da para desfazer.
   */
  const handleRemoveRecord = async (id: number) => {
    try {
      await matrixBuilderApi.deleteMatrix(id);
      emit('delete', [id]);

      showRemoveRecordSuccessToast();
    } catch {
      showRemoveRecordErrorToast();
    }
  };

  defineExpose({
    confirmDelete,
    handleDeleteFrontend,
    handleRemoveRecord,
    showDeleteSuccessToast,
    showDeleteErrorToast,
    showRemoveRecordSuccessToast,
    showRemoveRecordErrorToast,
  });
</script>

<template>
  <DefaultTable class="divide-y divide-gray-02">
    <thead>
      <tr>
        <HeaderTableWithButtons
          id="header-table-with-buttons"
          colspan="9"
          :text="tableTitle"
          :order-options="orderOptions"
          :is-filter-active="isFilterActive"
          @update:sort="handleSortOptions"
          @update:filter="handleFilter"
          @filter-clicked="handleFilterClicked"
        />
      </tr>
      <tr v-if="revisions.length">
        <TableHeader text="Prefixo" class="text-xs/5" />
        <TableHeader text="Rev." class="text-xs/5" />
        <TableHeader text="Data da Elaboração" class="text-xs/5" />
        <TableHeader text="Elaboração" class="text-xs/5" />
        <TableHeader text="Data da Aprovação" class="text-xs/5" />
        <TableHeader text="Aprovação" class="text-xs/5" />
        <TableHeader text="Vigência" class="text-xs/5" />
        <TableHeader text="Status" class="text-xs/5" />
        <TableHeader text="Ações" class="text-xs/5" />
      </tr>
    </thead>
    <tbody class="bg-white-01 divide-y divide-gray-02">
      <tr v-if="!revisions.length">
        <td colspan="9" class="py-8">
          <SearchNoResults />
        </td>
      </tr>
      <HomologationTableRow
        v-for="revision in revisions"
        :key="revision.id"
        :revision="revision"
        :document-type="documentType"
        @delete="confirmDelete"
      />
    </tbody>
  </DefaultTable>
</template>
